import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_DRIVING_LICENSES
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const MAX_IMAGE_CHARS = 900000;

/** Map Appwrite document → license shape used by Fleet Maintenance. */
export const formatDrivingLicense = (doc) => ({
  id: doc.$id ?? doc.id,
  vehicleId: doc.vehicleId || '',
  plate: doc.plate || '',
  driver: doc.driver || '',
  licenseNumber: doc.licenseNumber || '',
  fullName: doc.fullName || '',
  licenseClass: doc.licenseClass || '',
  issueDate: doc.issueDate || '',
  expiryDate: doc.expiryDate || '',
  issuingAuthority: doc.issuingAuthority || '',
  nationalId: doc.nationalId || '',
  frontImage: doc.frontImage || '',
  frontImageName: doc.frontImageName || '',
  updatedByEmail: doc.updatedByEmail || '',
  updatedByName: doc.updatedByName || ''
});

const toPayload = (fields) => {
  const frontImageRaw = String(fields.frontImage || '');
  const frontImage =
    frontImageRaw.length <= MAX_IMAGE_CHARS ? frontImageRaw : '';

  return {
    vehicleId: String(fields.vehicleId || '').trim(),
    plate: String(fields.plate || '')
      .trim()
      .toUpperCase()
      .replace(/\s/g, ''),
    driver: String(fields.driver || '').trim(),
    licenseNumber: String(fields.licenseNumber || '').trim(),
    fullName: String(fields.fullName || '').trim(),
    licenseClass: String(fields.licenseClass || '').trim(),
    issueDate: String(fields.issueDate || '').trim(),
    expiryDate: String(fields.expiryDate || '').trim(),
    issuingAuthority: String(fields.issuingAuthority || '').trim(),
    nationalId: String(fields.nationalId || '').trim(),
    frontImage,
    frontImageName: String(fields.frontImageName || '').trim(),
    updatedByEmail: String(fields.updatedByEmail || '').trim().toLowerCase(),
    updatedByName: String(fields.updatedByName || '').trim()
  };
};

export const getDrivingLicenseErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

/** Find license by vehicleId, falling back to plate. */
export const findDrivingLicense = async ({ vehicleId, plate } = {}) => {
  const id = String(vehicleId || '').trim();
  const plateNorm = String(plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');

  const tryQuery = async (queries) => {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_DRIVING_LICENSES,
      [...queries, Query.limit(1)]
    );
    return response.documents?.[0] ? formatDrivingLicense(response.documents[0]) : null;
  };

  if (id) {
    const byId = await tryQuery([Query.equal('vehicleId', id)]);
    if (byId) return byId;
  }
  if (plateNorm) {
    return tryQuery([Query.equal('plate', plateNorm)]);
  }
  return null;
};

/** Fetch licenses for a vehicle (by id and/or plate). */
export const fetchDrivingLicenseForVehicle = async ({ vehicleId, plate } = {}) => {
  return findDrivingLicense({ vehicleId, plate });
};

/** List all driving licenses (paginated). */
export const fetchAllDrivingLicenses = async () => {
  const all = [];
  let cursor = null;
  const limit = 100;

  for (let page = 0; page < 50; page += 1) {
    const queries = [Query.limit(limit), Query.orderAsc('fullName')];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_DRIVING_LICENSES,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatDrivingLicense));
    if (docs.length < limit) break;
    cursor = docs[docs.length - 1].$id;
  }

  return all;
};

/** Create or update a driving license for a vehicle. */
export const upsertDrivingLicense = async (fields) => {
  const payload = toPayload(fields);
  if (!payload.vehicleId) {
    throw new Error('vehicleId is required.');
  }
  if (!payload.licenseNumber || !payload.fullName) {
    throw new Error('licenseNumber and fullName are required.');
  }

  const existing = await findDrivingLicense({
    vehicleId: payload.vehicleId,
    plate: payload.plate
  });

  if (existing?.id) {
    const updated = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_DRIVING_LICENSES,
      existing.id,
      payload
    );
    return formatDrivingLicense(updated);
  }

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_DRIVING_LICENSES,
    ID.unique(),
    payload
  );
  return formatDrivingLicense(created);
};
